<template>
  <div class="video-lessons container py-4">
    <!-- Alert -->
    <div v-if="alert.message" class="mb-3">
      <div
        :class="[
          'alert',
          alert.type === 'success' ? 'alert-success' : 'alert-danger',
        ]"
      >
        {{ alert.message }}
      </div>
    </div>

    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="text-pink">Video Lessons</h2>

      <!-- Filters -->
      <div class="row g-3 mb-4">
        <div class="col-md-3">
          <label class="form-label">Grade</label>
          <select
            class="form-select"
            v-model="newTopicEnrollment.grade_id"
            @change="fetchVideos"
          >
            <option value="">All Grade</option>
            <option v-for="g in grades" :key="g.id" :value="g.id">
              {{ g.level }}
            </option>
          </select>
        </div>

        <div class="col-md-4">
          <label class="form-label">Subject</label>
          <select
            class="form-select"
            v-model="newTopicEnrollment.subject_id"
            :disabled="!newTopicEnrollment.grade_id"
            @change="fetchVideos"
          >
            <option value="">All Subject</option>
            <option v-for="s in subjects" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>

        <div class="col-md-5">
          <label class="form-label">Topic</label>
          <select
            class="form-select"
            v-model="newTopicEnrollment.topic_id"
            :disabled="!newTopicEnrollment.subject_id"
            @change="fetchVideos"
          >
            <option value="">All Topic</option>
            <option v-for="t in topics" :key="t.id" :value="t.id">
              {{ t.title }}
            </option>
          </select>
        </div>
      </div>
      <button
        type="button"
        class="btn btn-pink"
        data-bs-toggle="modal"
        data-bs-target="#addVideoModal"
      >
        <i class="bi bi-plus-lg"></i> Add Video
      </button>
    </div>

    <!-- Add Video Modal -->
    <div
      class="modal fade"
      id="addVideoModal"
      tabindex="-1"
      aria-labelledby="addVideoModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog">
        <div class="modal-content bg-dark text-white border-pink">
          <div class="modal-header border-bottom-pink">
            <h5 class="modal-title" id="addVideoModalLabel">
              Add YouTube Video
            </h5>
            <button
              type="button"
              class="btn-close btn-close-white"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <AddVideo @video-added="onVideoAdded" />
        </div>
      </div>
    </div>
    <div class="mb-3">
      <input
        type="text"
        v-model="search"
        class="form-control"
        placeholder="Search by title, grade, or subject..."
      />
    </div>

    <div class="text-end text-pink mb-2">{{ totalVideos }} Videos</div>
    <!-- Video Table -->
    <div class="table-responsive mt-3">
      <table class="table table-dark table-hover align-middle">
        <thead class="text-pink">
          <tr>
            <!-- <th>ID</th> -->
            <th>Title</th>
            <th>Grade</th>
            <th>Subject</th>
            <th>Video</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <!-- Loading row -->
          <tr v-if="loading">
            <td :colspan="6" class="text-center py-4">
              <div class="spinner-border text-pink" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2 text-pink mb-0">Loading videos...</p>
            </td>
          </tr>

          <!-- Video rows -->
          <!-- <tr v-else v-for="video in videos" :key="video.id"> -->
          <tr v-else v-for="video in filteredVideos" :key="video.id">
            <!-- <td>{{ video.id }}</td> -->
            <td>{{ video.title }}</td>
            <td>{{ video.grade }}</td>
            <td>{{ video.subject }}</td>
            <td>
              <div class="d-flex align-items-center gap-2">
                <img
                  :src="getYoutubeThumbnail(video.link)"
                  alt="thumbnail"
                  width="80"
                  class="rounded"
                />
                <a
                  :href="video.link"
                  target="_blank"
                  class="text-danger fw-bold"
                >
                  <i class="bi bi-youtube"></i> Watch
                </a>
              </div>
            </td>
            <td>
              <button
                @click="editVideo(video)"
                class="btn btn-sm btn-outline-warning me-1"
                data-bs-toggle="modal"
                data-bs-target="#editVideoModal"
              >
                <i class="bi bi-pencil-square"></i>
              </button>
              <button
                @click="deleteVideo(video.id)"
                class="btn btn-sm btn-outline-danger"
              >
                <i class="bi bi-trash"></i>
              </button>
            </td>
          </tr>

          <!-- No videos row -->
          <tr v-if="!loading && videos.length === 0">
            <td :colspan="6" class="text-center py-4 text-pink">
              <i class="bi bi-camera-video-off fs-2"></i>
              <div class="mt-2">No Video Lessons Yet.</div>
              <small>Click "Add Video" to upload your first lesson.</small>
            </td>
          </tr>
        </tbody>
      </table>

      <div class="d-flex justify-content-center mt-4">
        <nav
          aria-label="Video pagination"
          class="mt-4 d-flex justify-content-center"
        >
          <ul class="pagination custom-pagination">
            <!-- Previous -->
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <a
                href="#"
                class="page-link"
                @click.prevent="fetchVideos(currentPage - 1)"
              >
                Previous
              </a>
            </li>

            <!-- Page Numbers -->
            <li
              v-for="page in lastPage"
              :key="page"
              class="page-item"
              :class="{ active: currentPage === page }"
            >
              <a href="#" class="page-link" @click.prevent="fetchVideos(page)">
                {{ page }}
              </a>
            </li>

            <!-- Next -->
            <li
              class="page-item"
              :class="{ disabled: currentPage === lastPage }"
            >
              <a
                href="#"
                class="page-link"
                @click.prevent="fetchVideos(currentPage + 1)"
              >
                Next
              </a>
            </li>
          </ul>
        </nav>
      </div>

      <!-- Edit Video Modal -->
      <div
        class="modal fade"
        id="editVideoModal"
        tabindex="-1"
        aria-labelledby="editVideoModalLabel"
        aria-hidden="true"
      >
        <div class="modal-dialog">
          <div class="modal-content bg-dark text-white border-pink">
            <div class="modal-header border-bottom-pink">
              <h5 class="modal-title" id="editVideoModalLabel">
                Edit YouTube Video
              </h5>
              <button
                type="button"
                class="btn-close btn-close-white"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <EditVideo :video="selectedVideo" @video-edited="onVideoEdited" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AddVideo from "./VideoLessonParts/AddVideo.vue";
import EditVideo from "./VideoLessonParts/EditVideo.vue";

export default {
  name: "VideoLessons",
  components: { AddVideo, EditVideo },

  data() {
    return {
      videos: [],
      loading: false,
      selectedVideo: null,
      alert: { type: "", message: "" },
      search: "",
      grades: [],
      subjects: [],
      topics: [],
      currentPage: 1,
      lastPage: 1,
      totalVideos: 0,

      newTopicEnrollment: {
        grade_id: "",
        subject_id: "",
        topic_id: "",
      },
    };
  },
  computed: {
    filteredVideos() {
      if (!this.search) return this.videos;

      const term = this.search.toLowerCase();
      return this.videos.filter(
        (v) =>
          v.title.toLowerCase().includes(term) ||
          v.grade.toLowerCase().includes(term) ||
          v.subject.toLowerCase().includes(term),
      );
    },
  },

  watch: {
    "newTopicEnrollment.grade_id"(val) {
      if (val) {
        this.fetchSubjectsByGrade();
      } else {
        this.subjects = [];
        this.topics = [];
      }
    },

    "newTopicEnrollment.subject_id"(val) {
      if (val) {
        this.fetchTopicsBySubject();
      } else {
        this.topics = [];
      }
    },
  },
  methods: {
    async fetchGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchSubjectsByGrade() {
      try {
        const res = await axios.get(
          `/a-grade-subjects?grade_id=${this.newTopicEnrollment.grade_id}`,
        );
        // Flatten subjects if nested
        this.subjects =
          res.data.subjects ||
          res.data.map((s) => ({ id: s.subject.id, name: s.subject.name }));
      } catch (err) {
        console.error(err);
      }
    },

    async fetchTopicsBySubject() {
      try {
        const res = await axios.get(
          `/a-get-subject-topics?subject_id=${this.newTopicEnrollment.subject_id}`,
        );
        this.topics =
          res.data.topics ||
          res.data.map((t) => ({ id: t.id, title: t.title }));
      } catch (err) {
        console.error(err);
      }
    },

    getYoutubeThumbnail(url) {
      if (!url) return "";

      const regExp = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([^&?/]+)/;

      const match = url.match(regExp);

      return match
        ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
        : "";
    },
    async fetchVideos(page = 1) {
      this.loading = true;

      try {
        const res = await axios.get("/get-video-lessons", {
          params: {
            page: page, // ✅ send page
            grade_id: this.newTopicEnrollment.grade_id,
            subject_id: this.newTopicEnrollment.subject_id,
            topic_id: this.newTopicEnrollment.topic_id,
          },
        });

        this.videos = res.data.data;
        this.currentPage = res.data.current_page;
        this.lastPage = res.data.last_page;
        this.totalVideos = res.data.total; // ✅ get total
      } catch (err) {
        console.error(err);
        this.videos = [];
      } finally {
        this.loading = false;
      }
    },

    editVideo(video) {
      this.selectedVideo = video;
    },

    async deleteVideo(id) {
      if (!confirm("Are you sure?")) return;
      try {
        await axios.delete(`/delete-video/${id}`);
        await this.fetchVideos();
        this.videos = this.videos.filter((v) => v.id !== id);
        this.alert = {
          type: "success",
          message: "Video deleted successfully!",
        };
        setTimeout(() => (this.alert.message = ""), 3000);
      } catch (err) {
        console.error(err);
        // this.alert = { type: "error", message: "Failed to delete video!" };
        this.alert = { type: "danger", message: "Failed to delete video!" };
        setTimeout(() => (this.alert.message = ""), 3000);
      }
    },

    async onVideoAdded() {
      await this.fetchVideos();
      this.alert = { type: "success", message: "Video added successfully!" };
      setTimeout(() => (this.alert.message = ""), 3000);
    },

    async onVideoEdited(payload) {
      this.alert = payload;
      await this.fetchVideos();
      setTimeout(() => (this.alert.message = ""), 3000);
    },
  },

  mounted() {
    this.fetchGrades();
    this.fetchVideos();
  },
};
</script>

<style scoped>
.text-pink {
  color: #ff00cc !important;
}
.btn-pink {
  background-color: #ff00cc;
  color: white;
  border: none;
}
.btn-pink:hover {
  background-color: #e600b3;
  color: white;
}
.border-pink {
  border-color: #ff00cc !important;
}
.border-bottom-pink {
  border-bottom: 2px solid #ff00cc !important;
}

/* Pagination Theme */
.custom-pagination .page-link {
  background-color: #1a1a1a;
  color: #ff00cc;
  border: 1px solid #333;
  transition: 0.3s;
}

.custom-pagination .page-link:hover {
  background-color: #ff00cc;
  color: #fff;
  border-color: #ff00cc;
}

/* Active Page */
.custom-pagination .page-item.active .page-link {
  background-color: #ff00cc;
  border-color: #ff00cc;
  color: white;
  font-weight: bold;
}

/* Disabled */
.custom-pagination .page-item.disabled .page-link {
  background-color: #111;
  color: #666;
  border-color: #222;
  cursor: not-allowed;
}
</style>
